import { FeatureFlag, getClassroomFeatureFlags } from "@/api/classroomFeatureFlags";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

/**
 * Query to get the enabled feature flags for a given appliance ID.
 *
 * @example
 * ```ts
 * const featureFlagsQuery = useFeatureFlagsQuery("example-appliance-id");
 * // Check if the feature flag is enabled
 * featureFlagsQuery.data.includes("FEATURE_FLAG_NAME");
 * ```
 * @returns A query result with the enabled feature flags.
 */
export function useFeatureFlagsQuery(applianceId: string): UseQueryResult<FeatureFlag[]> {
	return useQuery({
		queryKey: ["classroomFeatureFlags", applianceId],
		queryFn: async () => {
			const res = await getClassroomFeatureFlags(applianceId);
			return res.enabledFeatures;
		},
		// feature flags rarely change and changes aren't needed immediately - only need to fetch them once per session (refresh site to re-fetch)
		// In order to use staleTime set to Infinity, we cannot provide an initialData, or it will never be fetched.
		cacheTime: Infinity,
		staleTime: Infinity,
	});
}
